import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { StockAdjustment, AdjustStockInput } from "../types";

const STOCK_ADJUSTMENT_FIELDS = `
  id
  organizationId
  productId
  warehouseId
  delta
  quantityAfter
  reason
  referenceType
  referenceId
  notes
  createdBy
  createdAt
`;

export const stockAdjustmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getStockAdjustments: builder.query<
      StockAdjustment[],
      { organizationId?: string; productId?: string; warehouseId?: string } | void
    >({
      query: (args) => ({
        body: {
          query: `
            query StockAdjustments($organizationId: String, $productId: String, $warehouseId: String) {
              stockAdjustments(organizationId: $organizationId, productId: $productId, warehouseId: $warehouseId) {
                ${STOCK_ADJUSTMENT_FIELDS}
              }
            }
          `,
          variables: {
            organizationId: args?.organizationId ?? null,
            productId: args?.productId ?? null,
            warehouseId: args?.warehouseId ?? null,
          },
        },
      }),

      transformResponse: (res: unknown) => pick<StockAdjustment[]>(res, "stockAdjustments"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "StockAdjustment" as const, id: s.id })),
              { type: "StockAdjustment" as const, id: "LIST" },
            ]
          : [{ type: "StockAdjustment" as const, id: "LIST" }],
    }),

    adjustStock: builder.mutation<StockAdjustment, AdjustStockInput>({
      query: (input) => ({
        body: {
          query: `
            mutation AdjustStock($input: AdjustStockInput!) {
              adjustStock(input: $input) {
                ${STOCK_ADJUSTMENT_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<StockAdjustment>(res, "adjustStock"),

      // A stock adjustment changes both the running balance (StockLevel) and
      // the product's derived totalStock — invalidate all three so every
      // screen showing them (this list, Stock Levels, Products) refetches.
      invalidatesTags: [
        { type: "StockAdjustment", id: "LIST" },
        { type: "StockLevel", id: "LIST" },
        { type: "Product", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStockAdjustmentsQuery,
  useLazyGetStockAdjustmentsQuery,
  useAdjustStockMutation,
} = stockAdjustmentApi;
